import { Progress } from 'antd'
import { GeckoMeasurementLabel } from 'features/fde'
import {
  MetricLabel,
  MetricValue,
} from 'features/inbox/components/shared/styledElements'
import { MetricDefinition } from 'features/inbox/config/inspectionMetrics'
import { EnrichedGeckoInspection } from 'features/inbox/types/types'
import React, { memo, useMemo } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { GeckoMeasurement } from 'types'

const MetricItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const ProgressBarContainer = styled.div`
  margin-top: 0.5rem;
`

interface InspectionMetricRendererProps {
  metric:
    | MetricDefinition<GeckoMeasurement>
    | MetricDefinition<string>
    | MetricDefinition<number>
    | MetricDefinition<number | undefined>
  inspection: EnrichedGeckoInspection
}

/**
 * Renders a single inspection metric based on its type
 * Supports 'value' (simple text display) and 'progress' (progress bar) components
 * Handles unit conversion based on user's measurement system preference
 */
const InspectionMetricRendererComponent: React.FC<
  InspectionMetricRendererProps
> = ({ metric, inspection }) => {
  const value = metric.getValue(inspection)

  const formattedValue = useMemo(() => {
    if (value instanceof GeckoMeasurement) {
      return <GeckoMeasurementLabel measurement={value} variant='narrow' />
    }
    // [The correlated union types problem] Type assertion is safe here because format and getValue share the same type parameter T
    // TypeScript can't infer that getValue's return type matches format's parameter type in union types
    return metric.format && value
      ? // @ts-expect-error - correlated union types https://github.com/microsoft/TypeScript/issues/30581
        metric.format(value, inspection)
      : String(value)
  }, [value, metric, inspection])

  if (metric.component === 'progress') {
    const progressValue =
      typeof value === 'string' ? parseFloat(value) : Number(value)

    return (
      <MetricItem key={metric.key}>
        <MetricLabel>{metric.label}</MetricLabel>
        <MetricValue>{formattedValue}</MetricValue>
        <ProgressBarContainer>
          <Progress
            percent={progressValue}
            size='default'
            showInfo={false}
            strokeColor={CantileverTheme.colors.alternate['200']}
            trailColor={CantileverTheme.colors.grayscale['600']}
          />
        </ProgressBarContainer>
      </MetricItem>
    )
  }

  // Default to 'value' component
  return (
    <MetricItem key={metric.key}>
      <MetricLabel>{metric.label}</MetricLabel>
      <MetricValue>{formattedValue}</MetricValue>
    </MetricItem>
  )
}

export const InspectionMetricRenderer = memo(InspectionMetricRendererComponent)
